import { z } from "zod";

/**
 * Feedback from inside the game (the game menu's "Send feedback"): a bug, an idea or anything
 * else, with where the player stood and, if they let it, a picture of the game view. Kept in
 * our own database and mailed to the team (apps/api).
 */
/** The form's tabs. */
export const FEEDBACK_KINDS = ["bug", "idea", "other"] as const;
/**
 * Every kind the api takes: the tabs, and a report of another player (Report on the target
 * frame; the App Store asks a game with chat for a way to report abuse).
 */
export const FEEDBACK_ALL_KINDS = [...FEEDBACK_KINDS, "report"] as const;
export type FeedbackKind = (typeof FEEDBACK_ALL_KINDS)[number];

export const FEEDBACK_MIN = 10;
export const FEEDBACK_MAX = 2000;
/** The game view picture, a JPEG as base64 (about 1280 px wide at 0.7 quality). */
export const FEEDBACK_SHOT_MAX = 900_000;

export const FeedbackSchema = z.strictObject({
  kind: z.enum(FEEDBACK_ALL_KINDS),
  text: z.string().trim().min(FEEDBACK_MIN).max(FEEDBACK_MAX),
  /** Where it happened, as the game saw it; every field optional and short. */
  context: z
    .strictObject({
      characterId: z.string().uuid().optional(),
      character: z.string().max(32).optional(),
      /** A report: the character it is about. */
      reported: z.string().max(32).optional(),
      server: z.string().max(32).optional(),
      zone: z.string().max(40).optional(),
      channel: z.int().min(1).max(9).optional(),
      x: z.number().finite().optional(),
      z: z.number().finite().optional(),
      level: z.int().min(1).max(100).optional(),
      cls: z.string().max(20).optional(),
      fps: z.number().min(0).max(1000).optional(),
      device: z.string().max(20).optional(),
      browser: z.string().max(40).optional(),
      os: z.string().max(40).optional(),
      screen: z.string().max(20).optional(),
      lang: z.string().max(5).optional(),
      release: z.string().max(40).optional(),
      /** The page visit: ties the feedback to that visit's error reports. */
      visit: z.string().uuid().optional(),
    })
    .default({}),
  screenshot: z
    .string()
    .max(FEEDBACK_SHOT_MAX)
    .regex(/^[A-Za-z0-9+/=]+$/)
    .optional(),
});
export type Feedback = z.infer<typeof FeedbackSchema>;
